import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories, siteName, siteSlogan } from "@/mocks/news";

const mainNav: { label: string; to: string }[] = [
  { label: "首页", to: "/" },
  { label: "今日热榜", to: "/#trending" },
  { label: "实时快讯", to: "/#live" },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="w-full sticky top-0 z-50">
      {/* 顶栏：直播状态 + 热搜词 */}
      <div className="hidden md:block bg-foreground-950 text-background-100">
        <div className="w-full px-4 md:px-6 h-9 flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 text-background-50">
              <span className="w-4 h-4 flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-primary-500 live-dot"></span>
              </span>
              7×24 小时持续更新
            </span>
            <span className="text-background-300/80">2026 年 9 月 10 日 · 星期四</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-background-300/80">热搜：</span>
            <div className="flex items-center gap-3">
              {["长夜行收官", "暴雨外卖免罚", "开源大模型", "音乐节抢票"].map((word) => (
                <Link
                  key={word}
                  to="/#trending"
                  className="text-background-200 hover:text-accent-400 transition-colors whitespace-nowrap"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <div
        className={`w-full bg-background-50 transition-all ${
          scrolled ? "border-b border-background-200 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]" : "border-b border-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-xl">
              <i className="ri-fire-fill"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-black text-lg text-foreground-950 tracking-tight">{siteName}</span>
              <span className="text-[10px] text-foreground-500 tracking-widest mt-0.5">{siteSlogan}</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
            {mainNav.map((item) =>
              item.to.includes("#") ? (
                <Link
                  key={item.label}
                  to={item.to}
                  className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                    location.pathname === item.to
                      ? "text-primary-600 font-medium"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}

            <div className="relative group">
              <button
                type="button"
                className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                频道
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>
              <div className="absolute right-0 top-full pt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <div className="bg-background-50 border border-background-200 rounded-lg p-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm text-foreground-900">{cat.name}</span>
                        <span className="text-xs text-foreground-500 line-clamp-1">{cat.desc}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden sm:flex items-center gap-2 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-megaphone-line"></i>
              </span>
              我要爆料
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="展开导航菜单"
              aria-expanded={menuOpen}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-background-200 bg-background-50">
            <div className="px-4 py-4 flex flex-col gap-1">
              {mainNav.map((item) =>
                item.to.includes("#") ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100"
                  >
                    {item.label}
                  </Link>
                )
              )}
              <div className="mt-2 pt-3 border-t border-background-200">
                <p className="px-3 pb-2 text-xs text-foreground-500 tracking-widest">内容频道</p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-100"
                    >
                      <i className={`${cat.icon} text-secondary-600`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}